'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TaskViewer, GeneralNotFound } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getTask } from 'src/api/admin/tasks';
import { getGroupTaskDocuments } from 'src/api/personal/groupDetail';

export const Route = createFileRoute('/personal_/groups/$groupId/tasks/$taskId')({
	loader: async ({ params }) => {
		const { groupId, taskId } = params;
		const task = await getTask(taskId);

		if (!task) {
			return { task: undefined, documents: [] };
		}

		const documents = await getGroupTaskDocuments(groupId, task.documentIds || []);
		return { task, documents };
	},
	component: TaskDetailPage,
});

function TaskDetailPage() {
	const { groupId, taskId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { task: initialTask, documents: initialDocuments } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const { data: task, refetch: refetchTask } = useQuery({
		queryKey: ['task', taskId],
		queryFn: () => getTask(taskId),
		initialData: initialTask,
	});

	const { data: documents } = useQuery({
		queryKey: ['personal-group-task-documents', groupId, task?.documentIds],
		queryFn: () => getGroupTaskDocuments(groupId, task?.documentIds || []),
		initialData: initialDocuments,
		enabled: !!task,
	});

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const handleUpdate = () => {
		refetchTask();
		queryClient.invalidateQueries({ queryKey: ['personal-group-tasks', groupId] });
		queryClient.invalidateQueries({ queryKey: ['personal-group', groupId] });
	};

	const handleClose = () => {
		navigate({
			to: '/personal/groups/$groupId/tasks',
			params: { groupId },
			search: { page: undefined, sort: undefined },
		});
	};

	const handleOpenDocument = (documentId: string) => {
		window.open(`/personal/groups/${groupId}/documents/${documentId}`, '_blank');
	};

	if (!task) {
		return (
			<GeneralNotFound
				label="We couldn't find this task. Maybe it has been deleted?"
				action={{
					label: 'Return to tasks',
					href: `/personal/groups/${groupId}/tasks`,
					variant: 'outlined',
				}}
			/>
		);
	}

	return (
		<TaskViewer
			task={task}
			documents={documents || []}
			onUpdate={handleUpdate}
			onClose={handleClose}
			onOpenDocument={handleOpenDocument}
			permissions={{
				taskDelete: verifyPermission([PERMISSIONS.personalGroupsEdit]),
				taskDo: verifyPermission([PERMISSIONS.personalGroupsView]),
			}}
		/>
	);
}
